import {PHASES, WAR_PHASE_STAGES} from "../../constants/phases";
import {
  selectBuildingsDamage,
  selectCitiesCache,
  selectCurrentPlayer,
  selectGameResults,
  selectGroundBackground,
  selectHexes,
  selectPendingSupply,
  selectPhase,
  selectPlayers,
  selectSelectedFarmsForConversion,
  selectSharedGameState,
  selectSpecialBuildings,
  selectUnits
} from "../../redux/reducers/sharedStateReducer/selectors";
import {calcFarmRemainingSupply,} from "../gameState/calcFarmRemainingSupply";
import {checkIfBarracksCanRecruitNewUnit} from "../gameState/checkIfBarracksCanRecruitNewUnit";
import {COMMON_BUILDING_TOKENS, TERRAIN_TOKENS} from "../../constants/tokens";
import {getMountainTile} from "../../configs/mountainTiles";
import {checkIfAdjacentToWorkingWarehouse} from "../gameState/supply/checkIfAdjacentToWorkingWarehouse";
import {
  checkIfAdjacentToWorkingMilitaryCamp
} from "../gameState/specialBuildings/checkIfAdjacentToWorkingMilitaryCamp";
import {findOwner} from "../gameState/findOwner";
import {
  FARM_SUPPLY_BLOCKED_COUNT,
  FARM_SUPPLY_STANDARD_COUNT,
  FARM_SUPPLY_WITH_WAREHOUSE_COUNT,
} from "../../constants/supply";
import {checkIfOwner} from "../gameState/checkIfOwner";
import {getActingPlayerFromState} from "../players/getActingPlayer";
import {
  checkIfParallelSupplyPhase,
  filterPlayerPendingSupply
} from "../gameState/supply/parallelSupply";
import {getConstructionPlanPlayer} from "../gameState/construction/planning/getConstructionPlanContext";
import {selectFieldWithConstructionPlan} from "../gameState/construction/planning/selectFieldWithConstructionPlan";
import {checkIfSupplyShownOnField} from "../gameState/supply/checkIfSupplyShownOnField";

// Картинки казармы. Обычная — один меч. Рядом с рабочим военным лагерем — два
// меча: отряд на такой казарме получает за снабжение удвоенный прирост размера
// (см. calcUnitUpdateBasedOnPendingSupply), как ферма у склада рисуется с двумя
// колосьями. У каждой — вариант этапа найма: казарма, где можно нанять отряд
const BARRACKS_IMAGE = COMMON_BUILDING_TOKENS.BARRACKS;
const BARRACKS_WITH_MILITARY_CAMP_IMAGE = 'barracks_2';
const BARRACKS_RECRUITMENT_IMAGE_SUFFIX = '_active';

/**
 * Все картинки казармы, какие может вернуть calculateTokenImage. Сцена обязана
 * загрузить каждую: незагруженную текстуру Phaser молча не рисует
 */
export const BARRACKS_TOKEN_IMAGES = [BARRACKS_IMAGE, BARRACKS_WITH_MILITARY_CAMP_IMAGE].flatMap(
  (image) => [image, `${image}${BARRACKS_RECRUITMENT_IMAGE_SUFFIX}`]
);

/**
 * Игроки, чье снабжение в ходе фазы снабжения показывается на поле этого
 * клиента: остаток снабжения на фермах и будущий размер отрядов.
 *
 * Поочередное снабжение — только текущий игрок, как было всегда.
 * Одновременное — сам пользователь (или бот, которым он управляет как админ),
 * и пока раздает, и после того, как закончил: чужую раздачу игрок, который
 * еще может ответить своей, видеть не должен. Зритель и запись матча ничего
 * не раздают — им видно снабжение всех игроков
 *
 * @param {Object} state
 * @returns {Object[]}
 */
export const getSupplyPreviewPlayers = (state) => {
  if (!checkIfParallelSupplyPhase(selectSharedGameState(state))) {
    const currentPlayer = selectCurrentPlayer(state);
    return currentPlayer ? [currentPlayer] : [];
  }

  const players = selectPlayers(state) || [];
  const actingPlayer = getActingPlayerFromState(state);
  const ownPlayer = actingPlayer && players.find((player) => player.id === actingPlayer.id);
  return ownPlayer ? [ownPlayer] : players;
};

/**
 * Отложенное снабжение игрока для расчетов «за него» (остаток ферм, отмены).
 * В одновременной фазе — только его записи; в поочередной — весь список, как
 * было всегда (раздавал его один текущий игрок)
 *
 * @param {Object} state
 * @param {Object|null} player
 * @returns {Object[]}
 */
export const selectSupplyPlayerPendingSupply = (state, player) => {
  const pendingSupply = selectPendingSupply(state) || [];
  const sharedGameState = selectSharedGameState(state);
  if (!player || !checkIfParallelSupplyPhase(sharedGameState)) {
    return pendingSupply;
  }
  return filterPlayerPendingSupply(pendingSupply, sharedGameState, player.id);
};

/**
 * Игрок, чей остаток снабжения показывает картинка фермы (farm_<остаток>_<лимит>),
 * или null — тогда ферма рисуется пустой. Это владелец фермы, если его
 * снабжение видно на этом клиенте (см. getSupplyPreviewPlayers)
 *
 * @param {Object} state
 * @param {string} farmHexKey
 * @returns {Object|null}
 */
export const resolveSupplyFarmImagePlayer = (state, farmHexKey) => {
  const citiesCache = selectCitiesCache(state) || {};
  return getSupplyPreviewPlayers(state)
    .find((player) => checkIfOwner(farmHexKey, player.color, citiesCache)) || null;
};

/**
 * Ключ текстуры жетона на ячейке — с учетом состояния партии.
 * @param {string} key - ключ ячейки
 * @param {string} tokenType - жетон из hexes
 * @param {Object} state - корневое состояние
 * @param {Object} [options]
 * @param {string|null} [options.groundBackgroundId] - подложка, которую
 *   показывает сцена: по ней берется тайл горы. Без нее — подложка из
 *   состояния (у сцены она может отличаться: пустую сцена заменяет случайной)
 */
export const calculateTokenImage = (key, tokenType, state, {groundBackgroundId} = {}) => {
  // Гора: один жетон, а картинка у каждой подложки поля своя — одна на все
  // горы поля (см. configs/mountainTiles)
  if (tokenType === TERRAIN_TOKENS.MOUNTAIN) {
    return getMountainTile(groundBackgroundId ?? selectGroundBackground(state));
  }

  const gameResults = selectGameResults(state);
  const phase = selectPhase(state);
  const sharedGameState = selectSharedGameState(state);
  const currentPlayer = selectCurrentPlayer(state);
  const citiesCache = selectCitiesCache(state);
  const hexes = selectHexes(state);
  const specialBuildings = selectSpecialBuildings(state);
  const players = selectPlayers(state);
  const units = selectUnits(state);
  const buildingsDamage = selectBuildingsDamage(state);

  if (tokenType === COMMON_BUILDING_TOKENS.FARM) {
    const farmSupplyLimit = getFarmSupplyLimit(key, state, currentPlayer);

    // Остаток считается за владельца фермы — со своими отложенным снабжением и
    // выбором к обмену по аграризму (в поочередной фазе это текущий игрок).
    // Во время облета захваченных городов фермы еще не активны, хотя
    // снабжение уже могло раздаться (checkIfSupplyShownOnField)
    const farmImagePlayer = !gameResults && checkIfSupplyShownOnField(state)
      ? resolveSupplyFarmImagePlayer(state, key)
      : null;
    if (farmImagePlayer) {
      const pendingSupply = selectSupplyPlayerPendingSupply(state, farmImagePlayer);
      const selectedFarmsForConversion = selectSelectedFarmsForConversion(state, farmImagePlayer.id);
      const remainingSupply = calcFarmRemainingSupply(key, selectedFarmsForConversion, hexes, specialBuildings, players, units, citiesCache, farmImagePlayer, pendingSupply, buildingsDamage);

      if (remainingSupply !== FARM_SUPPLY_BLOCKED_COUNT) {
        return `farm_${remainingSupply}_${farmSupplyLimit}`
      }
    }

    return `farm_0_${farmSupplyLimit}`
  }

  if (tokenType === COMMON_BUILDING_TOKENS.BARRACKS) {
    const barracksImage = getBarracksImage(key, state, currentPlayer);

    // В фазе войны на этапе найма заменяем отображение казарм
    const isRecruitmentStage = phase === PHASES.WAR
      && sharedGameState.warPhaseStage === WAR_PHASE_STAGES.RECRUITMENT;
    return isRecruitmentStage && checkIfBarracksCanRecruitNewUnit(key, state)
      ? `${barracksImage}${BARRACKS_RECRUITMENT_IMAGE_SUFFIX}`
      : barracksImage;
  }

  return tokenType;
}

/**
 * Стоит ли казарма рядом с рабочим военным лагерем — тогда отряд на ней
 * получает за снабжение удвоенный прирост размера, а сама она показана с
 * двумя мечами: и на поле (getBarracksImage), и в панели осмотра ячейки
 * (InspectedCellPanel). Проверка та же, по которой прирост считает редьюсер
 * снабжения (checkIfAdjacentToWorkingMilitaryCamp), — картинка не обещает
 * того, чего правила не дадут. Лагерь не работает, когда поврежден либо на нем
 * или рядом с ним стоит отряд противника владельца казармы.
 *
 * Лагерь ищется на поле таким, каким оно нарисовано, — вместе с планом
 * строительства (selectFieldWithConstructionPlan): запланированный лагерь уже
 * стоит на поле, и казарма рядом с ним рисуется так же, как нарисуется, когда
 * план применится. Так же ведет себя ферма у запланированного склада (см.
 * getFarmSupplyLimit).
 *
 * @param {string} barracksHexKey
 * @param {Object} state
 * @param {Object} fallbackOwner - чья казарма, если города о ней еще не знают
 *   (кэш городов стора пересчитывается только на завершении хода)
 * @returns {boolean}
 */
export const checkIfBarracksNearWorkingMilitaryCamp = (barracksHexKey, state, fallbackOwner) => {
  const field = selectFieldWithConstructionPlan(state);
  const citiesCache = field.citiesCache || selectCitiesCache(state);
  const owner = findOwner(barracksHexKey, selectPlayers(state) || [], citiesCache) || fallbackOwner;
  if (!owner) {
    return false;
  }

  return checkIfAdjacentToWorkingMilitaryCamp(
    barracksHexKey, owner, field.hexes, field.specialBuildings, citiesCache,
    selectUnits(state), selectBuildingsDamage(state)
  );
};

// Картинка казармы: рядом с рабочим военным лагерем — с двумя мечами, иначе
// обычная
const getBarracksImage = (barracksHexKey, state, fallbackOwner) => (
  checkIfBarracksNearWorkingMilitaryCamp(barracksHexKey, state, fallbackOwner)
    ? BARRACKS_WITH_MILITARY_CAMP_IMAGE
    : BARRACKS_IMAGE
);

/**
 * Сколько снабжения дает ферма: два рядом с рабочим складом, иначе одно.
 *
 * Склад ищется на поле таким, каким оно нарисовано, — вместе с планом
 * строительства (selectFieldWithConstructionPlan): запланированный склад уже
 * стоит на поле, и ферма рядом с ним рисуется так же, как нарисуется в свой
 * ход, когда план применится. Иначе картинка спорила бы и с самим полем, и с
 * плашкой снабжения, которая план учитывает (calcPlayerUnitsAndSupply).
 *
 * От владельца фермы зависит, чьи отряды блокируют склад. Пока план на поле,
 * владелец ищется по городам поля с планом: ферму мог привязать к городу
 * планирующего только запланированный квартал. Без плана — как было, по кэшу
 * городов стора.
 *
 * @param {string} farmHexKey
 * @param {Object} state
 * @param {Object} fallbackOwner - чья ферма, если города о ней еще не знают
 *   (кэш городов стора пересчитывается только на завершении хода)
 * @returns {number}
 */
const getFarmSupplyLimit = (farmHexKey, state, fallbackOwner) => {
  const field = selectFieldWithConstructionPlan(state);
  return checkIfAdjacentToWorkingWarehouse(
    farmHexKey, field.specialBuildings, selectUnits(state), selectPlayers(state),
    field.citiesCache || selectCitiesCache(state), fallbackOwner, field.hexes, selectBuildingsDamage(state)
  ) ? FARM_SUPPLY_WITH_WAREHOUSE_COUNT : FARM_SUPPLY_STANDARD_COUNT;
};

/**
 * Ключ текстуры ЗАПЛАНИРОВАННОГО жетона (план строительства). У плана картинка
 * — сам жетон, кроме фермы и казармы. Запас снабжения фермы зависит от склада
 * по соседству, прирост отряда на казарме — от военного лагеря; сосед может
 * быть построен или тоже запланирован. Картинка та же, что у построенного
 * жетона вне своих фаз (calculateTokenImage). Ферма и казарма в плане —
 * самого планирующего.
 *
 * Без состояния соседей не узнать — тогда картинка сам жетон: так план рисуют
 * там, где состояние партии не передают (renderPlannedBuildings принимает его
 * необязательным)
 *
 * @param {string} key - ключ ячейки
 * @param {string} token - запланированный жетон
 * @param {Object} [state] - корневое состояние
 */
export const calculatePlannedTokenImage = (key, token, state) => {
  const isFarm = token === COMMON_BUILDING_TOKENS.FARM;
  const isBarracks = token === COMMON_BUILDING_TOKENS.BARRACKS;
  if ((!isFarm && !isBarracks) || !state) {
    return token;
  }
  const planPlayer = getConstructionPlanPlayer(state) || selectCurrentPlayer(state);
  return isFarm
    ? `farm_0_${getFarmSupplyLimit(key, state, planPlayer)}`
    : getBarracksImage(key, state, planPlayer);
};
